import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import {
  Plus,
  Settings,
  Users,
  Home,
  Lightbulb,
  User,
  Shield,
  Bell,
  Keyboard,
} from 'lucide-react';

interface CommandPaletteProps {
  onNewDecision?: () => void;
  onTeamManagement?: () => void;
}

export const CommandPalette = ({ onNewDecision, onTeamManagement }: CommandPaletteProps) => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((open) => !open);
      }
      // N for new decision (when not in input)
      if (e.key === 'n' && !e.metaKey && !e.ctrlKey && !e.altKey) {
        const target = e.target as HTMLElement;
        if (target.tagName !== 'INPUT' && target.tagName !== 'TEXTAREA' && !target.isContentEditable) {
          e.preventDefault();
          onNewDecision?.();
        }
      }
    };

    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, [onNewDecision]);

  const runCommand = useCallback((command: () => void) => {
    setOpen(false);
    command();
  }, []);

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput placeholder="Suche nach Aktionen, Seiten..." />
      <CommandList>
        <CommandEmpty>Keine Ergebnisse gefunden.</CommandEmpty>
        
        <CommandGroup heading="Schnellaktionen">
          <CommandItem onSelect={() => runCommand(() => onNewDecision?.())}>
            <Plus className="mr-2 h-4 w-4" />
            <span>Neue Entscheidung starten</span>
            <kbd className="ml-auto pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border border-border/50 bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground">
              N
            </kbd>
          </CommandItem>
          <CommandItem onSelect={() => runCommand(() => onTeamManagement?.())}>
            <Users className="mr-2 h-4 w-4" />
            <span>Team verwalten</span>
          </CommandItem>
          <CommandItem onSelect={() => runCommand(() => navigate('/settings/workspace'))}>
            <Settings className="mr-2 h-4 w-4" />
            <span>Einstellungen öffnen</span>
          </CommandItem>
        </CommandGroup>

        <CommandSeparator />

        <CommandGroup heading="Navigation">
          <CommandItem onSelect={() => runCommand(() => navigate('/dashboard'))}>
            <Home className="mr-2 h-4 w-4" />
            <span>Dashboard</span>
          </CommandItem>
          <CommandItem onSelect={() => runCommand(() => navigate('/hypotheses'))}>
            <Lightbulb className="mr-2 h-4 w-4" />
            <span>Hypothesen</span>
          </CommandItem>
        </CommandGroup>

        <CommandSeparator />

        <CommandGroup heading="Einstellungen">
          <CommandItem onSelect={() => runCommand(() => navigate('/settings/profile'))}>
            <User className="mr-2 h-4 w-4" />
            <span>Profil</span>
          </CommandItem>
          <CommandItem onSelect={() => runCommand(() => navigate('/settings/workspace'))}>
            <Settings className="mr-2 h-4 w-4" />
            <span>Workspace</span>
          </CommandItem>
          <CommandItem onSelect={() => runCommand(() => navigate('/settings/team'))}>
            <Users className="mr-2 h-4 w-4" />
            <span>Team-Einstellungen</span>
          </CommandItem>
          <CommandItem onSelect={() => runCommand(() => navigate('/settings/notifications'))}>
            <Bell className="mr-2 h-4 w-4" />
            <span>Benachrichtigungen</span>
          </CommandItem>
          <CommandItem onSelect={() => runCommand(() => navigate('/settings/security'))}>
            <Shield className="mr-2 h-4 w-4" />
            <span>Sicherheit</span>
          </CommandItem>
        </CommandGroup>

        <CommandSeparator />

        <CommandGroup heading="Tastenkürzel">
          <CommandItem disabled className="opacity-60 cursor-default">
            <Keyboard className="mr-2 h-4 w-4" />
            <span className="flex-1">Befehlspalette</span>
            <kbd className="ml-auto pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border border-border/50 bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground">
              ⌘K
            </kbd>
          </CommandItem>
          <CommandItem disabled className="opacity-60 cursor-default">
            <Plus className="mr-2 h-4 w-4" />
            <span className="flex-1">Neue Entscheidung</span>
            <kbd className="ml-auto pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border border-border/50 bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground">
              N
            </kbd>
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
};
